import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import {
  deleteFolder,
  getFolderDeletePreview,
} from "../api/folders.api";
import type {
  FolderDeletePreview,
  StorageApiErrorBody,
  StorageErrorCode,
} from "../types/project-storage.types";
import { getStorageErrorMessage, mapStorageError } from "../utils/storageErrors";
import { folderTreeQueryKey } from "./useFolderTree";

export type DeleteFolderModalData = {
  folderId: string;
  displayName: string;
  subfolderCount: number;
  fileCount: number;
  requiresConfirmation: boolean;
};

export type DeleteFolderResult =
  | { ok: true }
  | { ok: false; errorCode?: StorageErrorCode; errorMessage: string };

function previewToModalData(preview: FolderDeletePreview): DeleteFolderModalData {
  return {
    folderId: preview.folderId,
    displayName: preview.displayName,
    subfolderCount: preview.subfolderCount,
    fileCount: preview.fileCount,
    requiresConfirmation: preview.requiresConfirmation,
  };
}

function errorBodyToModalData(
  folderId: string,
  displayName: string,
  body: StorageApiErrorBody
): DeleteFolderModalData | null {
  if (body.errorCode !== "FOLDER_NOT_EMPTY") return null;

  return {
    folderId,
    displayName,
    subfolderCount: body.subfolderCount ?? 0,
    fileCount: body.fileCount ?? 0,
    requiresConfirmation: body.requiresConfirmation ?? true,
  };
}

export function useDeleteFolder(mmCode: number | null) {
  const queryClient = useQueryClient();
  const [deleteModalData, setDeleteModalData] =
    useState<DeleteFolderModalData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const closeDeleteModal = useCallback(() => {
    if (isDeleting) return;
    setDeleteModalData(null);
  }, [isDeleting]);

  const openDeleteModal = useCallback(
    async (folderId: string, displayName: string) => {
      if (mmCode === null) return;

      setIsLoadingPreview(true);

      try {
        const preview = await getFolderDeletePreview(mmCode, folderId);
        setDeleteModalData(previewToModalData(preview));
      } catch (error) {
        toast.error(mapStorageError(error, "action").message);
      } finally {
        setIsLoadingPreview(false);
      }
    },
    [mmCode]
  );

  const confirmDeleteFolder = useCallback(
    async (modalData: DeleteFolderModalData): Promise<DeleteFolderResult> => {
      if (mmCode === null) {
        return {
          ok: false,
          errorMessage: "Não foi possível excluir a pasta.",
        };
      }

      setIsDeleting(true);

      try {
        await deleteFolder(
          mmCode,
          modalData.folderId,
          modalData.requiresConfirmation
        );

        await queryClient.invalidateQueries(folderTreeQueryKey(mmCode));
        setDeleteModalData(null);
        toast.success("Pasta excluída com sucesso.");
        return { ok: true };
      } catch (error) {
        const body = (error as any)?.response?.data as
          | StorageApiErrorBody
          | undefined;

        const retryModal = errorBodyToModalData(
          modalData.folderId,
          modalData.displayName,
          body ?? {}
        );

        if (retryModal) {
          setDeleteModalData(retryModal);
          return {
            ok: false,
            errorCode: "FOLDER_NOT_EMPTY",
            errorMessage: getStorageErrorMessage("FOLDER_NOT_EMPTY"),
          };
        }

        const mapped = mapStorageError(error, "action", "Não foi possível excluir a pasta.");
        toast.error(mapped.message);
        return {
          ok: false,
          errorCode: mapped.errorCode,
          errorMessage: mapped.message,
        };
      } finally {
        setIsDeleting(false);
      }
    },
    [mmCode, queryClient]
  );

  return {
    deleteModalData,
    isDeleting,
    isLoadingPreview,
    openDeleteModal,
    closeDeleteModal,
    confirmDeleteFolder,
  };
}
